// Settings › Campaigns › Your website (BL-092, AdTrack's DNI and form tags): Flowology's tag for the
// company's own site. It keeps each ad click, matches the call that follows on the number the visitor
// saw, and makes the site's forms leads with their click. Here: the tag to paste, the sites that may
// send, whether it shows each campaign's tracking number or only keeps the clicks, and the number for
// each campaign as its ads' URLs name it (adSessions.setup, adSessions.setSite). In Blueprint it's a part
// under the tracking numbers, in Riverstone a card of its own.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { useDesign } from '../blueprint/design.ts';
import type { TrackingNumber } from './campaigns-model.ts';
import {
  draftOf,
  siteInput,
  siteMeans,
  tagSnippet,
  weekLine,
  type SiteAnswer,
  type SiteDraft,
} from './site-model.ts';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');

export function SiteCapture({
  canEdit,
  numbers,
}: {
  canEdit: boolean;
  /** The company's tracking numbers, to choose each campaign's from. */
  numbers: TrackingNumber[];
}) {
  const bp = useDesign() === 'blueprint';
  const Part = bp ? 'div' : 'section';
  const [setup, setSetup] = useState<SiteAnswer | null>(null);
  const [draft, setDraft] = useState<SiteDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<SiteAnswer>('adSessions.setup', {})
      .then(setSetup)
      .catch((e: unknown) => setError(say(e)));
  }, []);
  const save = async (d: SiteDraft) => {
    const r = siteInput(d);
    if ('error' in r) return setError(r.error);
    setBusy(true);
    setError(null);
    try {
      setSetup(await client.call<SiteAnswer>('adSessions.setSite', r.input));
      setDraft(null);
      setSaid('Saved. Your site has it within five minutes.');
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const snippet = setup ? tagSnippet(window.location.origin, setup.tagPath) : '';
  const textOf = (phone: string) => numbers.find((n) => n.phone === phone)?.text ?? phone;
  const Heading = bp ? 'h3' : 'h2';

  return (
    <Part
      className={bp ? 'bp-part site-capture' : 'stone card site-capture'}
      role={bp ? 'group' : undefined}
      aria-label="Your website"
    >
      <div className="row between wrap">
        <Heading className={bp ? undefined : 'h3'}>Your website</Heading>
        {canEdit && setup && !draft && (
          <button
            type="button"
            className="btn soft"
            onClick={() => {
              setSaid(null);
              setDraft(draftOf(setup.site));
            }}
          >
            Change
          </button>
        )}
      </div>
      <p className="muted">
        Flowology’s tag on your site keeps each ad click, gives the call that follows its keyword, and makes
        your site’s forms leads with their click. Add it beside your other tags, in your tag manager or on
        every page.
      </p>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {!setup && !error && <p className="muted">Getting the website tag…</p>}
      {setup && !draft && (
        <>
          <p>
            <b>{siteMeans(setup.site)}</b>
            <span className="muted small block">{weekLine(setup.week)}</span>
          </p>
          <div className="site-tag">
            <code aria-label="The tag to add to your site">{snippet}</code>
            <button
              type="button"
              className="btn quiet"
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(snippet)
                  .then(() => setSaid('The tag is copied.'))
                  .catch(() => setError('Could not copy it: select the tag and copy it.'))
              }
            >
              Copy the tag
            </button>
          </div>
          {setup.site.origins.length > 0 && (
            <p className="small">
              <span className="muted">Sites: </span>
              {setup.site.origins.map((o) => o.replace(/^https?:\/\//, '')).join(', ')}
            </p>
          )}
          {setup.site.numbers.length > 0 && (
            <ul className="campaign-list" aria-label="Each campaign’s number on the site">
              {setup.site.numbers.map((n) => (
                <li key={n.key}>
                  <div>
                    <b>{n.key}</b>
                    <span className="muted small block">Shows {textOf(n.phone)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {draft && (
        <form
          className="fgrid campaign-form"
          aria-label="Change the website tag"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) void save(draft);
          }}
        >
          <label className="wide">
            <span className="flabel">Your sites, one a line</span>
            <textarea
              className="field"
              rows={2}
              value={draft.origins}
              placeholder="plumber.example"
              onChange={(e) => setDraft({ ...draft, origins: e.target.value })}
            />
          </label>
          <label className="check wide">
            <input
              type="checkbox"
              checked={draft.swap}
              onChange={(e) => setDraft({ ...draft, swap: e.target.checked })}
            />
            Show each campaign’s tracking number on the site. Off, it only keeps the clicks, while another tag
            shows the numbers.
          </label>
          <fieldset className="wide site-numbers">
            <legend className="flabel">Each campaign’s number, by the name its ads’ URLs give it</legend>
            {draft.numbers.map((n, i) => (
              <div key={i} className="row wrap">
                <input
                  className="field"
                  aria-label={`Campaign ${i + 1}, as the ads name it`}
                  placeholder="water_heaters"
                  maxLength={100}
                  value={n.key}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      numbers: draft.numbers.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)),
                    })
                  }
                />
                <select
                  className="field"
                  aria-label={`Number for campaign ${i + 1}`}
                  value={n.phone}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      numbers: draft.numbers.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)),
                    })
                  }
                >
                  <option value="">Choose a tracking number</option>
                  {numbers.map((t) => (
                    <option key={t.phone} value={t.phone}>
                      {t.label ? `${t.text} · ${t.label}` : t.text}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn quiet"
                  aria-label={`Remove campaign ${i + 1}`}
                  onClick={() => setDraft({ ...draft, numbers: draft.numbers.filter((_, j) => j !== i) })}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn soft"
              disabled={!numbers.length}
              onClick={() => setDraft({ ...draft, numbers: [...draft.numbers, { key: '', phone: '' }] })}
            >
              Add a campaign’s number
            </button>
            {!numbers.length && <p className="muted small">Add a tracking number above first.</p>}
          </fieldset>
          <div className="row wide">
            <SaveButton
              className="btn primary"
              busy={busy}
              busyLabel="Saving…"
              onClick={() => void save(draft)}
            >
              Save
            </SaveButton>
            <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Part>
  );
}
